import {
  React,
  useState,
  useEffect,
  useRef,
  useSelector,
  useDispatch,
  postRequestAuthenticated,
  postRequestAuthenticatedWithFile,
} from "../../../globals/imports.js";
import { toast } from "../../../utils/toast";
import { fetchbusinessCredentials } from "../../../redux/slices/businessSlice";
import UpgradeBanner, { hasGrowth } from "../widgets/UpgradeBanner";

// Only TRUSTED passthrough (no verification) is Growth-only. JWT SSO is on all
// plans — it needs a real server-side signing integration.
const GATED_AUTH_MODES = ["trusted"];

const Toggle = ({ checked, onChange, disabled }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    onClick={() => onChange(!checked)}
    className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
      checked ? "bg-theme-accent" : "bg-theme-tertiaryBackground"
    } ${disabled ? "opacity-50" : ""}`}
  >
    <span
      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
        checked ? "translate-x-6" : "translate-x-1"
      }`}
    />
  </button>
);

const Row = ({ label, desc, checked, onChange, saving }) => (
  <div className="flex items-center justify-between gap-4 border-b border-theme-chatDivider py-4 last:border-b-0">
    <div>
      <p className="text-sm font-medium text-theme-secondaryText">{label}</p>
      {desc && <p className="mt-0.5 text-xs text-theme-placeholder">{desc}</p>}
    </div>
    <Toggle checked={checked} onChange={onChange} disabled={saving} />
  </div>
);

const Field = ({ label, value }) => (
  <div>
    <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-theme-placeholder">
      {label}
    </p>
    <div className="rounded-lg border border-theme-chatDivider bg-theme-tertiaryBackground px-3 py-2.5 text-sm text-theme-secondaryText">
      {value || "—"}
    </div>
  </div>
);

const Input = ({ label, value, onChange, placeholder, type = "text" }) => (
  <div>
    <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-theme-placeholder">
      {label}
    </p>
    <input
      type={type}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full rounded-lg border border-theme-chatDivider bg-theme-tertiaryBackground px-3 py-2.5 text-sm text-theme-secondaryText outline-none placeholder:text-theme-placeholder"
    />
  </div>
);

// Only the flags the embed actually reads/renders — toggling these has a real
// effect and is consumed live by the embed via EmbedConfig.features. (channels/
// topics/chat/reactions exist in the model but aren't gated in the embed yet,
// so they're not shown here — no dead toggles.)
const FEATURE_LABELS = {
  channelSwitcher: {
    label: "Channel switcher",
    desc: "Let members switch between your channels from the header.",
  },
  showPublicChannels: {
    label: "Show public channels",
    desc: "Even with the switcher off, still list your public (Anyone) channels in the dropdown.",
    defaultOff: true,
  },
  dms: {
    label: "Direct messages",
    desc: "Members can DM each other and your team.",
  },
  talkToBrand: {
    label: "Talk to the brand",
    desc: 'Show a "Talk to us" button so members can message your team.',
  },
  memberDirectory: {
    label: "View members",
    desc: "Members can see the community member list.",
  },
  events: { label: "Events", desc: "Allow event posts inside topics." },
  polls: { label: "Polls", desc: "Allow polls inside topics." },
  memberPolls: {
    label: "Members can create polls",
    desc: "Let any member create polls in chat (off = only owner/admin).",
    defaultOff: true,
  },
  fileSharing: {
    label: "File sharing",
    desc: "Members can attach files in chat.",
  },
};

const THEME_MODES = ["light", "dark"];

const AUTH_MODES = [
  {
    key: "otp",
    label: "Email OTP",
    desc: "Members sign in with a one-time code emailed to them.",
  },
  {
    key: "google",
    label: "Google sign-in",
    desc: "Members sign in with their Google account.",
  },
  {
    key: "jwt",
    label: "JWT single sign-on",
    desc: "Your app signs a token with the secret below; members are logged in automatically.",
  },
  {
    key: "trusted",
    label: "Trusted passthrough",
    desc: "No verification — members you pass into the embed are trusted and logged in.",
  },
];

const SettingsTab = () => {
  const business = useSelector((state) => state.business.business);
  const myData = useSelector((state) => state.myData);
  const dispatch = useDispatch();

  // EmbedConfig (branding + features)
  const [branding, setBranding] = useState({
    displayName: "",
    logo: "",
    accentColor: "",
    accentTextColor: "",
    themeMode: "light",
  });
  const [features, setFeatures] = useState({});
  const [brandingSaving, setBrandingSaving] = useState(false);
  const [brandSaved, setBrandSaved] = useState(false);
  const [featSaving, setFeatSaving] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const logoInputRef = useRef(null);

  // EmbedConfig.auth (sign-in mode + JWT signing secret)
  const [auth, setAuth] = useState({ mode: "otp", jwtSecret: "" });
  const [authSaving, setAuthSaving] = useState(false);
  const [authSaved, setAuthSaved] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [secretCopied, setSecretCopied] = useState(false);

  // Editable business profile (name + support number)
  const [profile, setProfile] = useState({ name: "", supportNumber: "" });
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileSaved, setProfileSaved] = useState(false);

  // Channel-admin permissions (business.parameters). Whether channel admins may
  // delete channels — the creator/owner can always delete.
  const [allowAdminDelete, setAllowAdminDelete] = useState(false);
  const [permSaving, setPermSaving] = useState(false);

  useEffect(() => {
    setAllowAdminDelete(!!business?.parameters?.allowAdminDeleteChannel);
  }, [business?.parameters?.allowAdminDeleteChannel]);

  const saveAllowAdminDelete = async (value) => {
    setAllowAdminDelete(value);
    setPermSaving(true);
    try {
      const res = await postRequestAuthenticated(
        "/update/business/customizations",
        { allowAdminDeleteChannel: value }
      );
      if (res && res.success === false) {
        setAllowAdminDelete(!value);
        toast(res.message || "Couldn't save permissions.", "error");
        return;
      }
      // Keep redux business in sync so the toggle survives a remount.
      dispatch(fetchbusinessCredentials());
    } catch (e) {
      setAllowAdminDelete(!value);
    } finally {
      setPermSaving(false);
    }
  };

  useEffect(() => {
    setProfile({
      name: business?.name || "",
      supportNumber: business?.supportNumber || myData?.whatsapp_number || "",
    });
  }, [business?.name, business?.supportNumber, myData?.whatsapp_number]);

  const saveProfile = async () => {
    setProfileSaving(true);
    try {
      await postRequestAuthenticated("/update/business/profile", {
        name: profile.name,
        supportNumber: profile.supportNumber,
      });
      setProfileSaved(true);
      setTimeout(() => setProfileSaved(false), 1500);
    } finally {
      setProfileSaving(false);
    }
  };

  useEffect(() => {
    let active = true;
    postRequestAuthenticated("/embed/config", {})
      .then((res) => {
        if (active && res?.success && res.config) {
          const b = res.config.branding || {};
          setBranding({
            displayName: b.displayName || "",
            logo: b.logo || "",
            accentColor: b.accentColor || "",
            accentTextColor: b.accentTextColor || "",
            themeMode: b.themeMode || "light",
          });
          setFeatures(res.config.features || {});
          if (res.config.auth) {
            setAuth({
              mode: res.config.auth.mode || "otp",
              jwtSecret: res.config.auth.jwtSecret || "",
            });
          }
        }
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  const uploadLogo = async (file) => {
    if (!file) return;
    setLogoUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await postRequestAuthenticatedWithFile("/upload/image", fd);
      if (res?.success && res.url) {
        setBranding((b) => ({ ...b, logo: res.url }));
      }
    } finally {
      setLogoUploading(false);
    }
  };

  const saveBranding = async () => {
    setBrandingSaving(true);
    try {
      await postRequestAuthenticated("/embed/config/update", { branding });
      setBrandSaved(true);
      setTimeout(() => setBrandSaved(false), 1500);
    } finally {
      setBrandingSaving(false);
    }
  };

  // Selecting a mode only updates local state now; the explicit Save button
  // persists it.
  const canPassthrough = hasGrowth(business);
  const selectAuthMode = (mode) => {
    // Don't let a non-Growth business select a passthrough mode — the backend
    // would reject the save anyway.
    if (GATED_AUTH_MODES.includes(mode) && !canPassthrough) {
      toast("Trusted passthrough auto-login is available on the Growth plan.", "error");
      return;
    }
    setAuth((a) => ({ ...a, mode }));
  };

  const saveAuth = async () => {
    setAuthSaving(true);
    try {
      const res = await postRequestAuthenticated("/embed/config/update", {
        auth: { mode: auth.mode },
      });
      // The backend rejects gated modes for non-Growth plans — surface that
      // instead of a false "Saved ✓".
      if (res && res.success === false) {
        toast(res.message || "Couldn't save authentication settings.", "error");
        return;
      }
      setAuthSaved(true);
      setTimeout(() => setAuthSaved(false), 1500);
    } finally {
      setAuthSaving(false);
    }
  };

  const regenerateSecret = async () => {
    setAuthSaving(true);
    try {
      const res = await postRequestAuthenticated("/embed/config/update", {
        regenerateJwtSecret: true,
      });
      if (res?.success && res.config?.auth?.jwtSecret) {
        setAuth((a) => ({ ...a, jwtSecret: res.config.auth.jwtSecret }));
        setShowSecret(true);
      }
    } finally {
      setAuthSaving(false);
    }
  };

  const copySecret = () => {
    if (!auth.jwtSecret) return;
    navigator.clipboard?.writeText(auth.jwtSecret).then(() => {
      setSecretCopied(true);
      setTimeout(() => setSecretCopied(false), 1500);
    });
  };

  const toggleFeature = async (key, value) => {
    const next = { ...features, [key]: value };
    setFeatures(next);
    setFeatSaving(true);
    try {
      await postRequestAuthenticated("/embed/config/update", {
        features: { [key]: value },
      });
    } catch (e) {
      setFeatures(features);
    } finally {
      setFeatSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-4xl px-4 py-7 sm:px-6 lg:px-8">
      <div className="mb-7">
        <h1 className="text-2xl font-semibold text-theme-secondaryText">
          Settings
        </h1>
        <p className="mt-1 text-sm text-theme-placeholder">
          Your business profile, embed branding, and community preferences.
        </p>
      </div>

      <div className="flex flex-col gap-5">
        {/* Business profile */}
        <div className="rounded-2xl border border-theme-chatDivider bg-theme-secondaryBackground p-5">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-theme-secondaryText">
              Business profile
            </h3>
            <button
              onClick={saveProfile}
              disabled={profileSaving}
              className="rounded-lg bg-theme-accent px-4 py-1.5 text-xs font-semibold text-theme-accentText transition-colors hover:bg-theme-accentHover disabled:opacity-50"
            >
              {profileSaving ? "Saving…" : profileSaved ? "Saved ✓" : "Save"}
            </button>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input
              label="Business name"
              value={profile.name}
              onChange={(v) => setProfile((p) => ({ ...p, name: v }))}
              placeholder="Your business"
            />
            <Field label="Owner email" value={myData?.email} />
            <Field label="Domain" value={business?.domain} />
            <Input
              label="Support number"
              value={profile.supportNumber}
              onChange={(v) => setProfile((p) => ({ ...p, supportNumber: v }))}
              placeholder="+1 555 000 0000"
            />
          </div>
        </div>

        {/* Permissions (business.parameters) */}
        <div className="rounded-2xl border border-theme-chatDivider bg-theme-secondaryBackground p-5">
          <h3 className="mb-1 text-sm font-semibold text-theme-secondaryText">
            Permissions
          </h3>
          <p className="mb-2 text-xs text-theme-placeholder">
            Control what channel admins can do in your community.
          </p>
          <Row
            label="Allow admins to delete channels"
            desc="When on, a channel admin can delete channels. The channel creator can always delete."
            checked={allowAdminDelete}
            onChange={saveAllowAdminDelete}
            saving={permSaving}
          />
        </div>

        {/* Branding (EmbedConfig) */}
        <div className="rounded-2xl border border-theme-chatDivider bg-theme-secondaryBackground p-5">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-theme-secondaryText">
              Embed branding
            </h3>
            <button
              onClick={saveBranding}
              disabled={brandingSaving}
              className="rounded-lg bg-theme-accent px-4 py-1.5 text-xs font-semibold text-theme-accentText transition-colors hover:bg-theme-accentHover disabled:opacity-50"
            >
              {brandingSaving ? "Saving…" : brandSaved ? "Saved ✓" : "Save"}
            </button>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input
              label="Display name"
              value={branding.displayName}
              onChange={(v) => setBranding((b) => ({ ...b, displayName: v }))}
              placeholder="Acme Community"
            />
            <div>
              <Input
                label="Logo"
                value={branding.logo}
                onChange={(v) => setBranding((b) => ({ ...b, logo: v }))}
                placeholder="https://…/logo.png"
              />
              <div className="mt-2 flex items-center gap-3">
                {branding.logo ? (
                  <img
                    src={branding.logo}
                    alt="logo"
                    className="h-10 w-10 flex-shrink-0 rounded-lg border border-theme-chatDivider object-cover"
                  />
                ) : null}
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    uploadLogo(e.target.files?.[0]);
                    e.target.value = "";
                  }}
                />
                <button
                  type="button"
                  onClick={() => logoInputRef.current?.click()}
                  disabled={logoUploading}
                  className="rounded-lg border border-theme-chatDivider px-3 py-1.5 text-xs font-medium text-theme-secondaryText transition-colors hover:bg-theme-tertiaryBackground disabled:opacity-50"
                >
                  {logoUploading ? "Uploading…" : "Upload image"}
                </button>
                <span className="text-[11px] text-theme-placeholder">
                  Paste a URL or upload a file
                </span>
              </div>
            </div>
            <div>
              <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-theme-placeholder">
                Theme
              </p>
              <div className="inline-flex w-max rounded-lg border border-theme-chatDivider p-1">
                {THEME_MODES.map((m) => (
                  <button
                    key={m}
                    onClick={() => setBranding((b) => ({ ...b, themeMode: m }))}
                    className={`rounded-md px-4 py-1.5 text-sm font-medium capitalize transition-colors ${
                      branding.themeMode === m
                        ? "bg-theme-accent text-theme-accentText"
                        : "text-theme-primaryText hover:bg-theme-tertiaryBackground"
                    }`}
                  >
                    {m}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Features (EmbedConfig) */}
        <div className="rounded-2xl border border-theme-chatDivider bg-theme-secondaryBackground p-5">
          <h3 className="mb-1 text-sm font-semibold text-theme-secondaryText">
            Features
          </h3>
          <p className="mb-2 text-xs text-theme-placeholder">
            Turn embed features on or off for your community.
          </p>
          {Object.entries(FEATURE_LABELS).map(
            ([key, { label, desc, defaultOff }]) => (
              <Row
                key={key}
                label={label}
                desc={desc}
                checked={
                  defaultOff ? features[key] === true : features[key] !== false
                }
                onChange={(v) => toggleFeature(key, v)}
                saving={featSaving}
              />
            )
          )}
        </div>

        {/* Authentication (EmbedConfig.auth) */}
        <div className="rounded-2xl border border-theme-chatDivider bg-theme-secondaryBackground p-5">
          <div className="mb-1 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-theme-secondaryText">
              Authentication
            </h3>
            <button
              onClick={saveAuth}
              disabled={authSaving}
              className="rounded-lg bg-theme-accent px-4 py-1.5 text-xs font-semibold text-theme-accentText transition-colors hover:bg-theme-accentHover disabled:opacity-50"
            >
              {authSaving ? "Saving…" : authSaved ? "Saved ✓" : "Save"}
            </button>
          </div>
          <p className="mb-3 text-xs text-theme-placeholder">
            How people sign in to your embedded community.
          </p>
          {!canPassthrough && (
            <div className="mb-3">
              <UpgradeBanner feature="Trusted passthrough auto-login" />
            </div>
          )}
          <div className="flex flex-col gap-2">
            {AUTH_MODES.map((m) => {
              const gated = GATED_AUTH_MODES.includes(m.key) && !canPassthrough;
              return (
                <button
                  key={m.key}
                  onClick={() => selectAuthMode(m.key)}
                  disabled={authSaving || gated}
                  className={`flex items-start gap-3 rounded-xl border p-3 text-left transition-colors ${
                    auth.mode === m.key
                      ? "border-theme-accent bg-theme-accentSoft"
                      : "border-theme-chatDivider hover:bg-theme-tertiaryBackground"
                  } ${gated ? "cursor-not-allowed opacity-50" : ""}`}
                >
                  <span
                    className={`mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full border ${
                      auth.mode === m.key
                        ? "border-theme-accent"
                        : "border-theme-placeholder"
                    }`}
                  >
                    {auth.mode === m.key && (
                      <span className="h-2 w-2 rounded-full bg-theme-accent" />
                    )}
                  </span>
                  <span>
                    <span className="flex items-center gap-2 text-sm font-medium text-theme-secondaryText">
                      {m.label}
                      {gated && (
                        <span className="rounded-full bg-theme-tertiaryBackground px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-theme-placeholder">
                          Growth
                        </span>
                      )}
                    </span>
                    <span className="block text-xs text-theme-placeholder">
                      {m.desc}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>

          {auth.mode === "jwt" && (
            <div className="mt-4 rounded-xl border border-theme-chatDivider bg-theme-tertiaryBackground p-4">
              <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-theme-placeholder">
                Signing secret
              </p>
              <div className="flex items-stretch gap-2">
                <div className="flex-1 truncate rounded-lg border border-theme-chatDivider bg-theme-secondaryBackground px-3 py-2.5 font-mono text-xs text-theme-secondaryText">
                  {auth.jwtSecret
                    ? showSecret
                      ? auth.jwtSecret
                      : "•".repeat(32)
                    : "—"}
                </div>
                <button
                  onClick={() => setShowSecret((v) => !v)}
                  className="rounded-lg border border-theme-chatDivider px-3 py-2.5 text-xs font-semibold text-theme-primaryText hover:bg-theme-secondaryBackground"
                >
                  {showSecret ? "Hide" : "Show"}
                </button>
                <button
                  onClick={copySecret}
                  className="rounded-lg border border-theme-chatDivider px-3 py-2.5 text-xs font-semibold text-theme-primaryText hover:bg-theme-secondaryBackground"
                >
                  {secretCopied ? "Copied" : "Copy"}
                </button>
              </div>
              <p className="mt-3 text-xs text-theme-placeholder">
                On your server, sign an HS256 JWT with this secret carrying{" "}
                <span className="font-mono">external_id</span> (or{" "}
                <span className="font-mono">email</span>),{" "}
                <span className="font-mono">name</span>, and{" "}
                <span className="font-mono">image</span>, then pass it to the
                embed as <span className="font-mono">token</span>. Never expose
                this secret in the browser.
              </p>
              <button
                onClick={regenerateSecret}
                disabled={authSaving}
                className="mt-3 rounded-lg border border-theme-chatDivider px-3 py-1.5 text-xs font-semibold text-theme-error transition-colors hover:bg-theme-secondaryBackground disabled:opacity-50"
              >
                Regenerate secret
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SettingsTab;
